import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import chevronLeftIconUrl from './Images/chevron-left.svg';
import chevronRightIconUrl from './Images/chevron-right.svg';
import {
  getStandardsIconStyle,
  standardsClassNames,
  standardsStyles,
} from './Standards.styles';
import {
  Standard,
  Breakout,
  StandardWithBreakouts,
  getTemplateExpectations,
  getTemplateBreakouts,
} from './StandardsService';

export interface StandardsProps {
  standards?: StandardWithBreakouts[];
  standardSetId?: number | string;
  standardSetLabel?: string;
  templateId?: number | string;
  gradeId?: number | string;
  showBreakouts?: boolean;
  breakouts?: boolean;
  maxStandards?: number;
  select?: number;
  onExpandStandards?: () => void;
  onExpectationHeightChange?: (height: number) => void;
  onStandardsContentChange?: (hasStandards: boolean) => void;
}

const DEFAULT_CHARS_PER_STANDARD = 5;
const MAX_STANDARDS = 13;

export const getMaxStandards = (): number => MAX_STANDARDS;

function getVisibleStandardCount(
  baseStandardCount: number,
  firstStandardIndex: number,
  standardCount: number
): number {
  if (standardCount === 0) {
    return baseStandardCount;
  }

  let visibleStandardCount = baseStandardCount;

  if (firstStandardIndex > 0) {
    visibleStandardCount -= 1;
  }

  if (firstStandardIndex + visibleStandardCount < standardCount) {
    visibleStandardCount -= 1;
  }

  return Math.max(1, visibleStandardCount);
}

function getLastFilledFirstStandardIndex(baseStandardCount: number, standardCount: number): number {
  for (let index = Math.max(0, standardCount - 1); index > 0; index--) {
    const visibleCount = getVisibleStandardCount(baseStandardCount, index, standardCount);
    if (index + visibleCount <= standardCount) {
      return index;
    }
  }

  return 0;
}

export const getPagedStandardIndexes = (
  baseStandardCount: number,
  firstStandardIndex: number,
  standardCount: number
) => {
  const visibleStandardCount = getVisibleStandardCount(
    baseStandardCount,
    firstStandardIndex,
    standardCount
  );

  return {
    visibleStandardCount,
    lastStandardIndex: Math.min(standardCount - 1, firstStandardIndex + visibleStandardCount - 1),
  };
};

function sortByLevel(a: Standard, b: Standard, level: number): number {
  const aCode = a.expectation_code.split('.');
  const bCode = b.expectation_code.split('.');

  if (aCode[level] && bCode[level]) {
    const aNum = parseInt(aCode[level], 10);
    const bNum = parseInt(bCode[level], 10);

    if (!isNaN(aNum) && !isNaN(bNum)) {
      if (aNum !== bNum) {
        return aNum - bNum;
      }
      return sortByLevel(a, b, level + 1);
    }

    const stringComp = aCode[level].localeCompare(bCode[level]);
    if (stringComp !== 0) {
      return stringComp;
    }
    return sortByLevel(a, b, level + 1);
  }

  return 0;
}

function sortStandards(standards: Standard[]): Standard[] {
  return [...standards].sort((a, b) => sortByLevel(a, b, 0));
}

export const Standards = ({
  standards: propStandards,
  standardSetId,
  standardSetLabel,
  templateId,
  gradeId,
  showBreakouts = false,
  breakouts: useBreakoutsFromProps = false,
  maxStandards = MAX_STANDARDS,
  select,
  onExpandStandards,
  onExpectationHeightChange,
  onStandardsContentChange,
}: StandardsProps) => {
  const theme = useTheme();

  const [expanded, setExpanded] = useState(false);
  const [experienceStandards, setExperienceStandards] = useState<Standard[]>([]);
  const [experienceBreakouts, setExperienceBreakouts] = useState<Breakout[]>([]);
  const [hasStandardsContent, setHasStandardsContent] = useState(
    Array.isArray(propStandards) && propStandards.length > 0
  );
  const [selectedStandard, setSelectedStandard] = useState<Standard | null>(null);
  const [firstStandardIndex, setFirstStandardIndex] = useState(0);
  const expectationContainerRef = useRef<HTMLDivElement | null>(null);

  const numDisplayStandards = useMemo(() => {
    if (experienceStandards.length === 0) return maxStandards;

    const avgCharCount =
      experienceStandards.reduce((sum, std) => sum + std.expectation_code.length, 0) /
      experienceStandards.length;

    return Math.min(
      maxStandards,
      Math.round((maxStandards * DEFAULT_CHARS_PER_STANDARD) / avgCharCount)
    );
  }, [experienceStandards, maxStandards]);

  const { visibleStandardCount, lastStandardIndex } = getPagedStandardIndexes(
    numDisplayStandards,
    firstStandardIndex,
    experienceStandards.length
  );

  const selectedBreakouts = useMemo(() => {
    if (!selectedStandard) return [];
    return experienceBreakouts.filter((breakout) => selectedStandard.id === breakout.expectation_id);
  }, [selectedStandard, experienceBreakouts]);

  const handleExpandStandards = useCallback(() => {
    onExpandStandards?.();
    setExpanded(true);
    setFirstStandardIndex(0);
  }, [onExpandStandards]);

  const handleCollapseStandards = useCallback(() => {
    setExpanded(false);
    setSelectedStandard(null);
  }, []);

  const handleExpandCollapseStandards = useCallback(() => {
    if (expanded) {
      handleCollapseStandards();
    } else {
      handleExpandStandards();
    }
  }, [expanded, handleCollapseStandards, handleExpandStandards]);

  const handleSelectStandard = useCallback(
    (standard: Standard) => {
      if (showBreakouts) {
        setSelectedStandard(standard);
      }
    },
    [showBreakouts]
  );

  const handleNextPage = useCallback(() => {
    setFirstStandardIndex((prev) => {
      const currentVisibleStandardCount = getVisibleStandardCount(
        numDisplayStandards,
        prev,
        experienceStandards.length
      );
      return Math.min(
        prev + currentVisibleStandardCount,
        getLastFilledFirstStandardIndex(numDisplayStandards, experienceStandards.length)
      );
    });
    setSelectedStandard(null);
  }, [numDisplayStandards, experienceStandards.length]);

  const handlePrevPage = useCallback(() => {
    setFirstStandardIndex((prev) => {
      const currentVisibleStandardCount = getVisibleStandardCount(
        numDisplayStandards,
        prev,
        experienceStandards.length
      );
      return Math.max(0, prev - currentVisibleStandardCount);
    });
    setSelectedStandard(null);
  }, [numDisplayStandards, experienceStandards.length]);

  const formatExpectation = (expectation: string | undefined): string => {
    if (!expectation) return '';
    return expectation.charAt(0).toUpperCase() + expectation.slice(1) + '. Specifically:';
  };

  const getBreakoutCodeIndex = (breakout: Breakout): string => breakout.breakout_code.split('.').pop() || '';

  useEffect(() => {
    if (!selectedStandard || !expectationContainerRef.current) {
      onExpectationHeightChange?.(0);
      return undefined;
    }

    const expectationElement = expectationContainerRef.current;
    const updateExpectationHeight = () => {
      const measuredHeight = Math.ceil(expectationElement.getBoundingClientRect().height);
      onExpectationHeightChange?.(measuredHeight + parseFloat(theme.spacing(1)));
    };

    updateExpectationHeight();
    window.addEventListener('resize', updateExpectationHeight);

    if (typeof ResizeObserver === 'undefined') {
      return () => {
        window.removeEventListener('resize', updateExpectationHeight);
      };
    }

    const resizeObserver = new ResizeObserver(updateExpectationHeight);
    resizeObserver.observe(expectationElement);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateExpectationHeight);
    };
  }, [selectedStandard, selectedBreakouts, onExpectationHeightChange, theme]);

  useEffect(() => {
    const standardsArray = Array.isArray(propStandards) ? propStandards : [];
    const updateStandardsContent = (hasContent: boolean) => {
      setHasStandardsContent(hasContent);
      onStandardsContentChange?.(hasContent);
    };

    if (standardsArray.length > 0) {
      updateStandardsContent(true);
      const expectations = standardsArray.map((std) => std.expectation);
      setExperienceStandards(sortStandards(expectations));

      if (useBreakoutsFromProps) {
        const allBreakouts = standardsArray.flatMap((std) => std.breakouts);
        const sortedBreakouts = allBreakouts.sort((a, b) => a.breakout_order - b.breakout_order);
        setExperienceBreakouts(sortedBreakouts);
      } else if (templateId) {
        getTemplateBreakouts(templateId as number)
          .then((templateBreakouts) => {
            const sortedBreakouts = templateBreakouts.sort((a, b) => a.breakout_order - b.breakout_order);
            setExperienceBreakouts(sortedBreakouts);
          })
          .catch(console.error);
      } else {
        setExperienceBreakouts([]);
      }

      if (select !== undefined && select >= 0 && select < standardsArray.length) {
        handleExpandStandards();
        setSelectedStandard(standardsArray[select].expectation);
      }
    } else if (templateId && standardSetId && gradeId) {
      updateStandardsContent(false);
      getTemplateExpectations(templateId as number, standardSetId as number, gradeId as number)
        .then((templateStandards) => {
          setExperienceStandards(sortStandards(templateStandards));
          updateStandardsContent(templateStandards.length > 0);
        })
        .catch(console.error);

      getTemplateBreakouts(templateId as number)
        .then((templateBreakouts) => {
          const sortedBreakouts = templateBreakouts.sort((a, b) => a.breakout_order - b.breakout_order);
          setExperienceBreakouts(sortedBreakouts);
        })
        .catch(console.error);
    } else {
      updateStandardsContent(false);
    }
  }, [
    useBreakoutsFromProps,
    propStandards,
    templateId,
    standardSetId,
    gradeId,
    select,
    handleExpandStandards,
    onStandardsContentChange,
  ]);

  if (!hasStandardsContent) {
    return null;
  }

  const standardsLabel = standardSetLabel || 'Standards';

  return (
    <Box sx={standardsStyles.container(theme)}>
      <Box sx={standardsStyles.controlsRow(theme)}>
        <Box
          component="button"
          type="button"
          sx={standardsStyles.standardSetLabel(theme, expanded)}
          onClick={handleExpandCollapseStandards}
          aria-expanded={expanded}
        >
          {standardSetLabel}
        </Box>

        {!expanded && (
          <Box
            component="button"
            type="button"
            sx={standardsStyles.expandButton(theme)}
            onClick={handleExpandStandards}
            aria-label={`Expand ${standardsLabel} standards`}
          >
            <Box
              component="span"
              className={standardsClassNames.chevronIcon}
              sx={standardsStyles.chevronIcon()}
              style={getStandardsIconStyle(chevronRightIconUrl)}
              aria-hidden="true"
            />
          </Box>
        )}

        {expanded && (
          <Box sx={standardsStyles.expandedStandardsRow(theme)}>
            {firstStandardIndex > 0 && (
              <Box
                component="button"
                type="button"
                sx={standardsStyles.scrollButton(theme)}
                onClick={handlePrevPage}
                aria-label={`Show previous ${standardsLabel} standards`}
              >
                <Box
                  component="span"
                  className={standardsClassNames.chevronIcon}
                  sx={standardsStyles.chevronIcon()}
                  style={getStandardsIconStyle(chevronLeftIconUrl)}
                  aria-hidden="true"
                />
              </Box>
            )}

            {experienceStandards.map((standard, index) => {
              if (index < firstStandardIndex || index > lastStandardIndex) {
                return null;
              }

              const isSelected = selectedStandard?.id === standard.id;

              return (
                <Box
                  key={standard.id}
                  component="button"
                  type="button"
                  sx={standardsStyles.standardButton(theme, isSelected, !showBreakouts)}
                  onClick={() => handleSelectStandard(standard)}
                  aria-pressed={isSelected}
                >
                  {standard.expectation_code}
                </Box>
              );
            })}

            {lastStandardIndex < experienceStandards.length - 1 && (
              <Box
                component="button"
                type="button"
                sx={standardsStyles.scrollButton(theme)}
                onClick={handleNextPage}
                aria-label={`Show next ${standardsLabel} standards`}
              >
                <Box
                  component="span"
                  className={standardsClassNames.chevronIcon}
                  sx={standardsStyles.chevronIcon()}
                  style={getStandardsIconStyle(chevronRightIconUrl)}
                  aria-hidden="true"
                />
              </Box>
            )}
          </Box>
        )}
      </Box>

      {selectedStandard && (
        <Box
          ref={expectationContainerRef}
          className="standards-expectation-container"
          sx={standardsStyles.expectationContainer(theme)}
        >
          <Typography sx={standardsStyles.expectationLabel(theme)}>
            {formatExpectation(selectedStandard.expectation_label)}
          </Typography>
          {selectedBreakouts.map((breakout) => (
            <Typography
              key={breakout.breakout_code}
              sx={standardsStyles.breakoutItem(theme)}
            >
              <Box component="strong">{selectedStandard.expectation_code}</Box>
              <Box component="span" sx={standardsStyles.breakoutText(theme)}>
                ({getBreakoutCodeIndex(breakout)}) {breakout.breakout_label}
              </Box>
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Standards;
